import { Fragment, ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import { ReactComponent as LogoHelpdesk } from "../../assets/img/XC_Helpdesk.svg";
import { ReactComponent as LogoHelpdeskIcone } from "../../assets/img/XC_Helpdesk_sm.svg";
import { getSessionData } from "../../utils/storageUtils";
import { MenuPermissao, PERMISSOES_POR_PERFIL } from "../../types/PermissaoInterface";

export const LARGURA_MENU = 310;
export const LARGURA_MENU_FECHADO = 60;

// Cor de destaque do item ativo/hover (mesma do XC WMS)
const COR_DESTAQUE = "#F88604";

interface ItemMenu {
  label: string;
  icone?: ReactNode;
  rota?: string;
  subItens?: ItemMenu[];
  permissao?: keyof MenuPermissao;
}

const itensMenu: ItemMenu[] = [
  {
    label: "Cadastro",
    icone: <PeopleAltOutlinedIcon fontSize="small" />,
    permissao: "cadastro",
    subItens: [
      { label: "Empresa", rota: "/cadastro/empresa" },
      { label: "Usuário", rota: "/cadastro/usuario" },
      { label: "Códigos do Sistema", rota: "/cadastro/codigodosistema" },
    ],
  },
  {
    label: "Painel de Controle",
    icone: <DashboardOutlinedIcon fontSize="small" />,
    rota: "/kpi/dashboard/paineldecontrole",
    permissao: "paineldecontrole",
  },
  {
    label: "Tickets",
    icone: <ConfirmationNumberOutlinedIcon fontSize="small" />,
    rota: "/tickets",
    permissao: "tickets",
  },
];

const estiloItem = {
  minHeight: 40,
  py: 0,
  mb: 0.5,
  borderRadius: "8px",
  color: "inherit",
  "&:hover": { bgcolor: COR_DESTAQUE, color: "inherit" },
  "&.Mui-selected, &.Mui-selected:hover": { bgcolor: COR_DESTAQUE },
};

interface MenuLateralProps {
  // Menu expandido (desktop)
  aberto: boolean;
  // Em telas pequenas o menu abre por cima do conteúdo
  mobile: boolean;
  abertoMobile: boolean;
  onFecharMobile: () => void;
}

// Menu lateral da área logada (mesmo padrão do XC WMS)
const MenuLateral = ({ aberto, mobile, abertoMobile, onFecharMobile }: MenuLateralProps) => {
  const theme = useTheme();
  const location = useLocation();
  const [hover, setHover] = useState(false);

  const perfil = (getSessionData("PerfilUsuario") || "usuario").toLowerCase();
  const permissoes = PERMISSOES_POR_PERFIL[perfil] ?? {
    cadastro: false,
    paineldecontrole: false,
    tickets: false,
  };
  const itensVisiveis = itensMenu.filter(
    (item) => !item.permissao || permissoes[item.permissao]
  );

  // Fechado, o menu expande temporariamente ao passar o mouse
  const expandido = mobile || aberto || hover;

  const rotaAtiva = (rota?: string) =>
    !!rota && (location.pathname === rota || location.pathname.startsWith(`${rota}/`));
  const grupoAtivo = (item: ItemMenu) =>
    !!item.subItens?.some((subItem) => rotaAtiva(subItem.rota));

  const [gruposAbertos, setGruposAbertos] = useState<Record<string, boolean>>({});

  // Abre o grupo da tela atual
  useEffect(() => {
    itensMenu.forEach((item) => {
      if (grupoAtivo(item)) {
        setGruposAbertos((atual) => ({ ...atual, [item.label]: true }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const alternarGrupo = (label: string) =>
    setGruposAbertos((atual) => ({ ...atual, [label]: !atual[label] }));

  const aoNavegar = mobile ? onFecharMobile : undefined;

  const conteudo = (
    <>
      <Box
        sx={{
          height: 64,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 1,
          // O texto "HELP DESK" do logo acompanha a cor do tema (claro/escuro)
          "& [fill='#383837']": { fill: theme.palette.text.primary },
        }}
      >
        <Link to="/" onClick={aoNavegar} style={{ display: "flex" }} aria-label="XC Help Desk">
          {expandido ? (
            <LogoHelpdesk style={{ height: 34, width: "auto" }} />
          ) : (
            <LogoHelpdeskIcone style={{ height: 36, width: 36 }} />
          )}
        </Link>
      </Box>
      <Divider />
      <List sx={{ px: 1, py: 1 }}>
        {itensVisiveis.map((item) =>
          item.subItens ? (
            <Fragment key={item.label}>
              <ListItemButton
                onClick={() => alternarGrupo(item.label)}
                selected={!expandido && grupoAtivo(item)}
                sx={estiloItem}
              >
                <ListItemIcon sx={{ minWidth: 44, color: "inherit" }}>{item.icone}</ListItemIcon>
                {expandido && (
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500 }} />
                )}
                {expandido && (gruposAbertos[item.label] ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
              <Collapse in={expandido && !!gruposAbertos[item.label]} timeout="auto">
                <List disablePadding>
                  {item.subItens.map((subItem) => (
                    <ListItemButton
                      key={subItem.label}
                      component={Link}
                      to={subItem.rota!}
                      onClick={aoNavegar}
                      selected={rotaAtiva(subItem.rota)}
                      sx={{ ...estiloItem, minHeight: 34, pl: 7.5 }}
                    >
                      <ListItemText primary={subItem.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </Fragment>
          ) : (
            <ListItemButton
              key={item.label}
              component={Link}
              to={item.rota!}
              onClick={aoNavegar}
              selected={rotaAtiva(item.rota)}
              sx={estiloItem}
            >
              <ListItemIcon sx={{ minWidth: 44, color: "inherit" }}>{item.icone}</ListItemIcon>
              {expandido && (
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500 }} />
              )}
            </ListItemButton>
          )
        )}
      </List>
    </>
  );

  if (mobile) {
    return (
      <Drawer
        variant="temporary"
        open={abertoMobile}
        onClose={onFecharMobile}
        ModalProps={{ keepMounted: true }}
        // Abaixo dos modais react-bootstrap (backdrop 1050)
        sx={{
          zIndex: 1045,
          "& .MuiDrawer-paper": {
            width: LARGURA_MENU,
            boxSizing: "border-box",
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        {conteudo}
      </Drawer>
    );
  }

  const larguraMenu = aberto ? LARGURA_MENU : LARGURA_MENU_FECHADO;

  return (
    <Drawer
      variant="permanent"
      onMouseEnter={() => !aberto && setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        width: larguraMenu,
        flexShrink: 0,
        transition: "width 0.3s",
        "& .MuiDrawer-paper": {
          width: expandido ? LARGURA_MENU : LARGURA_MENU_FECHADO,
          boxSizing: "border-box",
          overflowX: "hidden",
          transition: "width 0.3s",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          backgroundColor: theme.palette.background.paper,
          // Abaixo dos modais react-bootstrap (backdrop 1050)
          zIndex: 1040,
        },
      }}
    >
      {conteudo}
    </Drawer>
  );
};

export default MenuLateral;
